<template>
  <q-layout id="app" view="hHh lpR fFf">
    <q-header class="bg-dark app-header">
      <q-toolbar>
        <q-toolbar-title shrink class="cursor-pointer row items-center no-wrap" @click="router.push('/')">
          <img src="@/assets/atdi-logo.png" alt="ATDI" class="app-logo q-mr-sm">
          ATDI Licenses
        </q-toolbar-title>

        <template v-if="session">
          <q-btn
            v-for="item in navItems"
            :key="item.path"
            flat
            :to="item.path"
            :label="item.label"
            :class="{ 'nav-item--active': isSection(item.path) }"
          />
        </template>

        <q-space />

        <q-btn v-if="session" flat icon="account_box" :label="session.username">
          <q-menu>
            <q-list>
              <q-item v-close-popup clickable @click="logUserOut">
                <q-item-section>Log out</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </q-toolbar>
    </q-header>

    <q-page-container>
      <q-page padding :style-fn="pageStyleFn">
        <!-- Held back until the reference data is in, because several screens read the store once
             on mount and would otherwise render zeros. The Loading plugin covers the wait. -->
        <router-view v-if="!appData.loadingInitData" />
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useQuasar } from "quasar"

import { useSessionStore } from "@/stores/userSession.js"
import { useAppDataStore } from "@/stores/appData.js"

const $q = useQuasar()
const router = useRouter()
const route = useRoute()
const sessionStore = useSessionStore()
const appData = useAppDataStore()

const session = computed(() => sessionStore.session)

// The token lasts a week and refreshIfNeeded only acts inside the last day of it, so this is a
// cheap check rather than a rotation. The old timer refreshed unconditionally every 20 minutes,
// which churned tokens for no benefit and gave a second tab something to collide with.
const REFRESH_CHECK_INTERVAL_MS = 60 * 60 * 1000

let refreshIntervalHandler = null

const hasRole = (role) => {
  return sessionStore.hasRole(role)
}

// QBtn accepts `to` but not router-link's active classes - Quasar hands it only the
// non-matching link props - so which section is current is worked out here.
const NAV_ITEMS = [
  { path: "/customers", label: "Customers", role: "customers_view" },
  { path: "/licenses-list", label: "Licenses list", role: "licenses_view" },
  { path: "/software", label: "Software", role: "software_view" },
  { path: "/admin-panel", label: "Admin panel", role: "admin_view" }
]

const navItems = computed(() => NAV_ITEMS.filter((item) => hasRole(item.role)))

// The section, not the exact path, so /admin-panel/:tab keeps its item lit.
const isSection = (path) => route.path === path || route.path.startsWith(`${path}/`)

// A definite page height - rather than Quasar's default min-height - lets tables inside
// cap themselves at the space left over and scroll their own body. Quasar supplies the
// header/footer offset, so there is no hardcoded chrome height here.
const pageStyleFn = (offset, height) => ({
  height: height ? `${height - offset}px` : `calc(100vh - ${offset}px)`
})

const logUserOut = async () => {
  // Leaving the page is driven by the watcher below rather than from here, so that a sign-out in
  // another tab - which arrives as the session simply going away - is treated the same way.
  await sessionStore.logOut()
}

// Only a refusal ends the session. Anything else - a timeout, a 502 from the proxy, a restart
// mid-tick - leaves it alone and the next check tries again, where before any failure at all
// signed the user out with no retry.
const refreshSession = async () => {
  try {
    await sessionStore.refreshIfNeeded()
  } catch (error) {
    if (error?.response?.status === 401) await logUserOut()
  }
}

// Reference data reloads whenever a session appears - covers a fresh login; onMounted covers a
// reload. Losing one sends the user to the login page wherever it went, including a sign-out or an
// expiry picked up from another tab through the storage listener in main.js.
watch(
  () => sessionStore.session != null,
  (isLogged) => {
    if (isLogged) {
      appData.fetchInitData()
    } else if (router.currentRoute.value.name !== "login") {
      router.push({ name: "login" })
    }
  }
)

// Quasar's global overlay while the reference data loads, in place of an inline spinner.
watch(
  () => appData.loadingInitData,
  (loading) => {
    if (loading) $q.loading.show({ message: "Loading data..." })
    else $q.loading.hide()
  }
)

onMounted(() => {
  if (sessionStore.session) {
    refreshSession()
    appData.fetchInitData()
  }
  refreshIntervalHandler = setInterval(refreshSession, REFRESH_CHECK_INTERVAL_MS)
})

onBeforeUnmount(() => {
  clearInterval(refreshIntervalHandler)
  // Never leave the overlay up if this unmounts mid-load.
  $q.loading.hide()
})
</script>

<style lang="scss" scoped>
.app-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.28);
}

.app-logo {
  height: 28px;
}

.nav-item--active {
  font-weight: 600;

  // .q-btn is already position: relative and uses no root pseudo-element.
  &::after {
    content: "";
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 4px;
    height: 2px;
    border-radius: 1px;
    background: currentColor;
  }
}
</style>
